/** Bound the entire operation, including token refresh and response parsing. */
export async function withRequestDeadline<T>(
  operation: (signal: AbortSignal) => Promise<T>,
  milliseconds = 20000,
  message = "Saving is taking longer than expected. Please try again; your edits are still here.",
  parent?: AbortSignal
): Promise<T> {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout>;
  let cancel: () => void = () => undefined;
  const interrupted = new Promise<never>((_, reject) => {
    cancel = () => {
      controller.abort();
      reject(new DOMException("Cancelled", "AbortError"));
    };
    parent?.addEventListener("abort", cancel, { once: true });
    timer = setTimeout(() => {
      controller.abort();
      reject(Object.assign(new Error(message), { name: "TimeoutError" }));
    }, milliseconds);
    if (parent?.aborted) cancel();
  });
  try {
    return await Promise.race([interrupted, operation(controller.signal)]);
  } finally {
    clearTimeout(timer!);
    parent?.removeEventListener("abort", cancel);
  }
}

/**
 * Deadlines surface as "TimeoutError", but some browsers (Chrome on iOS) report
 * an AbortSignal.timeout expiry as "AbortError".
 */
export function isTimeoutError(error: unknown): boolean {
  const name = (error as { name?: unknown } | null)?.name;
  return name === "TimeoutError" || name === "AbortError";
}
